import { useMemo } from "react";
import { useScopeParams } from "@/hooks/useScopeParams";
import { Tooltip } from "antd";
import DonutPlot from "@/components/CategoricalPlots";
import FetchWrapper from "@/components/UI/FetchWraper";
import Loader from "@/components/UI/Loader";
import ErrorComponent from "@/components/UI/Error";
import Error403 from "@/components/UI/Error403";
import { is403ApiError, useFetch } from "@/hooks/FetchHook";
import { NOT_ENROLLED_STATUSES } from "@/views/Subjects/Overview/Overview/constants";
import { TablePlot } from "@/views/TrialHomeScreen/components/TablePlots";
import { CardLayout } from "@/views/TrialHomeScreen/components/CardLayout";
import demoData from "@/views/TrialHomeScreen/demo_data.json";
import type { HomeData } from "@/views/TrialHomeScreen/TrialHomeScreen";
import { trials } from "@/utils/trials";
import DemoSafetySection from "@/views/TrialHomeScreen/sections/demo/SafetySection";
import {
  cloneTrialHomeData,
  getDoseHeldReducedStacks,
  getIpRelatedEvents,
} from "@/views/TrialHomeScreen/utils";
import BrokenLinkIcon from "@/assets/icons/broken_link.svg?react";

const WIDTH = 220;
const HEIGHT = 220;
const MARGIN = 36;
const DONUT_DIMENSIONS = { width: WIDTH, height: HEIGHT, margin: MARGIN };
const DONUT_CARD_CLASS =
  "bg-white rounded-xs relative shadow-omni h-[230px] flex items-center justify-center";

const payload = {
  dataset_type: "adverse_events",
  global_filters: [],
  pagination: { page: 1, page_size: 10000 },
};

export function MetricsTab() {
  const { id } = useScopeParams();
  const isDemoTrial = id === trials.demo.DARATUMUMAB;
  const {
    data: fetchedHomeData,
    isLoading: isHomeLoading,
    error: homeError,
  } = useFetch({
    endpointSuffix: "home",
    enabled: !!id && !isDemoTrial,
    useQueryOptions: {
      staleTime: 5 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: 2,
    } as any,
  });
  const cardData = useMemo(() => {
    const sourceData = (isDemoTrial ? demoData : fetchedHomeData) as
      | HomeData
      | null
      | undefined;

    if (!sourceData) {
      return null;
    }

    return cloneTrialHomeData(sourceData);
  }, [fetchedHomeData, isDemoTrial]);

  return (
    <FetchWrapper endpointSuffix="filters/data/" method="POST" body={payload}>
      {(data) => (
        <MetricsTabComponent
          data={data.results}
          cardData={cardData}
          trialId={id}
          isHomeLoading={isHomeLoading}
          homeError={homeError}
        />
      )}
    </FetchWrapper>
  );
}

function MetricsTabComponent({
  data,
  cardData,
  trialId,
  isHomeLoading,
  homeError,
}: {
  readonly data: any[];
  readonly cardData: HomeData | null;
  readonly trialId?: string;
  readonly isHomeLoading: boolean;
  readonly homeError: unknown;
}) {
  const doseStacks = getDoseHeldReducedStacks(cardData || undefined);
  const bridgeBioIpRelatedEvents = cardData ? getIpRelatedEvents(cardData) : [];

  const adverse_events = useMemo(
    () =>
      data.map((d) => {
        return {
          ...d,
          isr_symptoms: d.isr_symptoms ? d.isr_symptoms.split(", ") : null,
          hypersensitivity_symptoms: d.hypersensitivity_symptoms
            ? d.hypersensitivity_symptoms.split(", ")
            : null,
          therapy: d.therapy ? d.therapy.split(", ") : null,
        };
      }),
    [data],
  );

  const enrolledAdverseEvents = adverse_events.filter(
    (d) => !NOT_ENROLLED_STATUSES.includes(d.study_status),
  );

  const getUniqueSubjectCount = (records: { subject: string }[]) =>
    new Set(records.map((record) => record.subject)).size;

  const totalSubjects = getUniqueSubjectCount(enrolledAdverseEvents);
  const subjectsWithAE = getUniqueSubjectCount(
    enrolledAdverseEvents.filter(
      (d) => d.aeyn === true && d.ae_data_source === "EDC",
    ),
  );
  // const subjectsWithSIAEs = getUniqueSubjectCount(
  //   enrolledAdverseEvents.filter(
  //     (d) => d.hypersensitivity_rxn === "True" || d.injection_site_rxn === "True",
  //   ),
  // );
  const subjectsWithSAE = getUniqueSubjectCount(
    enrolledAdverseEvents.filter((d) => d.serious === true),
  );
  const discontinuedSubjects = getUniqueSubjectCount(
    adverse_events.filter((d) => d.discont_reason === "Adverse event"),
  );
  const subjectsWithDeaths = getUniqueSubjectCount(
    adverse_events.filter((d) => d.serious_outcomes === "Death"),
  );

  const donutConfigs = [
    {
      className: "subject_with_ae adverse_events",
      text: ["Subjects", "with AEs"],
      data: {
        "Subjects with AEs": subjectsWithAE,
        "Subjects without AEs": Math.max(0, totalSubjects - subjectsWithAE),
      },
      valueOfInterest: "Subjects with AEs",
      centerLabel:
        "Number of subjects who have experienced an AE vs. total number of subjects enrolled",
    },
    // {
    //   className: "subject_with_siae adverse_events",
    //   text: ["Subjects", "with SIAEs"],
    //   data: {
    //     "Subjects with SIAEs": subjectsWithSIAEs,
    //     "Subjects with an AE that is not a SIAE": Math.max(
    //       0,
    //       totalSubjects - subjectsWithSIAEs,
    //     ),
    //   },
    //   valueOfInterest: "Subjects with SIAEs",
    //   centerLabel:
    //     "Number of subjects who have experienced an SIAE vs. total number of subjects enrolled",
    // },
    {
      className: "subject_with_sae adverse_events",
      text: ["Subjects", "with SAEs"],
      data: {
        "Subjects with SAEs": subjectsWithSAE,
        "Subjects with an AE that is not a SAE": Math.max(
          0,
          totalSubjects - subjectsWithSAE,
        ),
      },
      valueOfInterest: "Subjects with SAEs",
      centerLabel:
        "Number of subjects who have experienced an SAE vs. total number of subjects enrolled",
    },
    {
      className: "subject_discontinued adverse_events",
      text: ["Subjects", "Discontinued"],
      data: {
        "Discontinued subjects": discontinuedSubjects,
        "Subjects with an AE that have not discontinued": Math.max(
          0,
          totalSubjects - discontinuedSubjects,
        ),
      },
      valueOfInterest: "Discontinued subjects",
      centerLabel:
        "Number of subjects who have discontinued due to AE vs. total number of subjects enrolled",
    },
    {
      className: "subject_deaths adverse_events",
      text: ["Subject", "Deaths"],
      data: {
        "Subjects with an AE resulting in death": subjectsWithDeaths,
        "Subjects with an AE that did not result in death": Math.max(
          0,
          totalSubjects - subjectsWithDeaths,
        ),
      },
      valueOfInterest: "Subjects with an AE resulting in death",
      centerLabel:
        "Number of subjects who have experienced an AE resulting in death vs. total number of subjects in enrolled",
    },
  ];

  const cardIcon = (
    <Tooltip title="All workspace filters are ignored for this card">
      <span className="inline-flex">
        <BrokenLinkIcon className="w-4 h-4 cursor-default text-[#2B4752]" />
      </span>
    </Tooltip>
  );

  return (
    <div className="mt-5">
      <div className="w-full grid grid-cols-4 gap-[15px]">
        {donutConfigs.map((config) => (
          <div
            key={`${config.className}-${config.valueOfInterest}`}
            className={DONUT_CARD_CLASS}
          >
            <span className="absolute top-2 right-2">
              <Tooltip title="All workspace filters are ignored for this plot">
                <span className="inline-flex">
                  <BrokenLinkIcon className="w-4 h-4 cursor-default text-[#2B4752]" />
                </span>
              </Tooltip>
            </span>
            <DonutPlot
              addPercent
              className={config.className}
              text={config.text}
              data={config.data as any}
              dimensions={DONUT_DIMENSIONS}
              valueOfInterest={config.valueOfInterest}
              centerLabel={config.centerLabel}
            />
          </div>
        ))}
      </div>

      <div className="mt-5">
        {trialId === trials.demo.DARATUMUMAB && (
          <DemoSafetySection data={cardData} isAEHome={true} />
        )}
      </div>

      <div className="w-full grid grid-cols-3 gap-5 mt-5">
        {trialId === trials.bridgebio.PROPEL && isHomeLoading && (
          <div className="col-span-3 flex justify-center items-center">
            <Loader />
          </div>
        )}
        {trialId === trials.bridgebio.PROPEL && !!homeError && (
          <div className="col-span-3 flex justify-center items-center">
            {is403ApiError(homeError) ? <Error403 /> : <ErrorComponent />}
          </div>
        )}
        {trialId === trials.bridgebio.PROPEL &&
          !isHomeLoading &&
          !homeError &&
          cardData && (
            <>
              <div>
                <CardLayout
                  fullHeight={true}
                  title="Grade 3 TEAEs Outcomes"
                  subtitle="Outcomes for Grade 3 TEAEs for all time"
                  headerExtra={cardIcon}
                >
                  <div className="p-3 h-full">
                    <TablePlot
                      data={[cardData.taes || []]}
                      mode="counts"
                      categoriesTitle="LAB"
                      sorted={true}
                    />
                  </div>
                </CardLayout>
              </div>
              <div>
                <CardLayout
                  fullHeight={true}
                  title="SAEs Outcomes"
                  subtitle="Outcomes for any grade SAEs for all time"
                  headerExtra={cardIcon}
                >
                  <div className="p-3 h-full">
                    <TablePlot
                      data={[cardData.saes || []]}
                      mode="counts"
                      categoriesTitle="OUTCOME"
                      sorted={true}
                    />
                  </div>
                </CardLayout>
              </div>
              <div className="flex flex-col gap-4 lg:col-span-1 col-span-2">
                <CardLayout
                  fullHeight={true}
                  title="Doses Held & Reduced"
                  subtitle="Count of doses held/reduced due to AEs"
                  headerExtra={cardIcon}
                >
                  <div className="p-3 h-full">
                    <TablePlot
                      data={[cardData.doseHeldReduced || []]}
                      mode="counts"
                      categoriesTitle="REASON"
                      preDefinedStacks={doseStacks}
                      stackLabels={{
                        HELD: "Held",
                        REDUCED: "Reduced",
                        "Dose Held": "Held",
                        "Dose Reduced": "Reduced",
                      }}
                    />
                  </div>
                </CardLayout>
                <CardLayout
                  fullHeight={true}
                  title="Grade 3 TEAEs & SAEs Related to IP"
                  subtitle="Count of Grade 3 TEAEs & SAEs related to IP"
                  headerExtra={cardIcon}
                >
                  <div className="p-3 h-full">
                    <TablePlot
                      data={[bridgeBioIpRelatedEvents]}
                      mode="counts"
                      categoriesTitle="REASON"
                      noTitle
                    />
                  </div>
                </CardLayout>
              </div>
            </>
          )}
      </div>
    </div>
  );
}
